// @story: libs/storybook/stories/modules/auth/user-entity-access-panel.stories.ts
import { Dialog } from '@angular/cdk/dialog';
import { NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { catchError, forkJoin, map, of, type Observable } from 'rxjs';
import type { EntityType, UserEntityAccessRow } from '@zag/auth/models';
import {
  Avatar,
  Badge,
  Card,
  ConfirmDialogComponent,
  Dropdown,
  ToastService,
  TooltipDirective,
  ZagButton,
  ZagIcon,
  type AvatarVariant,
  type DropdownOption,
} from '@zag/ui';
import {
  EntityAccessDialogComponent,
  type EntityAccessDialogCloseResult,
  type EntityAccessDialogData,
} from '../entity-access-dialog/entity-access-dialog.component';
import { EntityAccessService, isFullModeRejection } from '../entity-access.service';
import { PermissionService } from '../permission.service';

interface PanelGrantRow {
  grant: UserEntityAccessRow;
  label: string | null;
}

interface PanelAlert {
  badgeKey: string;
  hintKey: string;
}

const TYPE_BADGE_KEYS: Record<EntityType, string> = {
  person: 'entityAccess.panel.typePerson',
  team: 'entityAccess.panel.typeTeam',
  department: 'entityAccess.panel.typeDepartment',
  calendar: 'entityAccess.panel.typeCalendar',
  location: 'entityAccess.panel.typeLocation',
  skill: 'entityAccess.panel.typeSkill',
  plan: 'entityAccess.panel.typePlan',
};

const ENTITY_TYPES: readonly EntityType[] = ['person', 'team', 'department', 'calendar', 'location', 'skill', 'plan'];

function rowKeyOf(entityType: EntityType, entityId: string, viaGroupId?: string | null): string {
  const base = `${entityType}:${entityId}`;
  return viaGroupId ? `${base}:via:${viaGroupId}` : base;
}

@Component({
  selector: 'sc-user-entity-access-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, NgTemplateOutlet, TranslatePipe, Avatar, Badge, Card, Dropdown, TooltipDirective, ZagButton, ZagIcon],
  templateUrl: './user-entity-access-panel.component.html',
  styleUrl: './user-entity-access-panel.component.scss',
})
export class UserEntityAccessPanelComponent {
  private readonly dialog = inject(Dialog);
  private readonly access = inject(EntityAccessService);
  private readonly permissions = inject(PermissionService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  readonly userId = input.required<string>();
  readonly userLabel = input<string>('');

  protected typeKey(entityType: EntityType): string {
    return TYPE_BADGE_KEYS[entityType];
  }

  private readonly currentLang = toSignal(this.translate.onLangChange, {
    initialValue: { lang: this.translate.currentLang, translations: {} },
  });

  protected readonly ready = this.permissions.isLoaded;
  protected readonly canView = computed(() => this.permissions.hasPermission('settings:update'));

  private readonly grantsResource = rxResource({
    params: () => ({ userId: this.userId(), ready: this.ready(), allowed: this.canView() }),
    stream: ({ params }) => (params.ready && params.allowed ? this.access.listUserAccess(params.userId) : of([] as UserEntityAccessRow[])),
    defaultValue: [] as UserEntityAccessRow[],
  });

  protected readonly grants = computed(() => this.grantsResource.value() ?? []);

  private readonly entityKeys = computed(() =>
    this.grants()
      .map(grant => rowKeyOf(grant.entityType, grant.entityId))
      .sort()
      .join('|')
  );

  private readonly labelsResource = rxResource({
    params: () => ({ keys: this.entityKeys() }),
    stream: ({ params }) => {
      if (!params.keys) return of([] as { key: string; label: string | null }[]);
      return forkJoin(
        params.keys.split('|').map(key => {
          const separator = key.indexOf(':');
          const entityType = key.slice(0, separator) as EntityType;
          const entityId = key.slice(separator + 1);
          return this.resolveLabel(entityType, entityId).pipe(
            map((label): { key: string; label: string | null } => ({ key, label })),
            catchError(() => of({ key, label: null as string | null }))
          );
        })
      );
    },
    defaultValue: [] as { key: string; label: string | null }[],
  });

  private readonly labelMap = computed(() => new Map((this.labelsResource.value() ?? []).map(entry => [entry.key, entry.label] as const)));

  protected readonly allRows = computed<PanelGrantRow[]>(() =>
    this.grants().map(grant => ({
      grant,
      label: this.labelMap().get(rowKeyOf(grant.entityType, grant.entityId)) ?? null,
    }))
  );

  protected readonly directRows = computed(() => this.allRows().filter(row => row.grant.viaGroup === null));
  protected readonly inheritedRows = computed(() => this.allRows().filter(row => row.grant.viaGroup !== null));

  protected readonly isLoading = computed(() => this.grantsResource.isLoading() || this.labelsResource.isLoading());
  protected readonly loadFailed = computed(() => this.grantsResource.error() != null);
  protected readonly hasRestricted = computed(() => this.allRows().some(row => row.label === null));
  protected readonly alert = signal<PanelAlert | null>(null);
  protected readonly deletingKey = signal<string | null>(null);

  protected readonly adding = signal(false);
  protected readonly addEntityType = signal<EntityType | null>(null);
  protected readonly addEntityId = signal<string | null>(null);

  protected readonly entityTypeOptions = computed<DropdownOption<EntityType>[]>(() => {
    this.currentLang();
    return ENTITY_TYPES.map(value => ({ label: this.translate.instant(TYPE_BADGE_KEYS[value]), value }));
  });

  private readonly entityOptionsResource = rxResource({
    params: () => ({ picking: this.adding(), entityType: this.addEntityType(), granted: this.entityKeys() }),
    stream: ({ params }) => {
      const entityType = params.entityType;
      if (!params.picking || !entityType) return of([] as DropdownOption<string>[]);
      const granted = new Set(params.granted ? params.granted.split('|') : []);
      return this.loadEntityOptions(entityType).pipe(
        map(options => options.filter(option => !granted.has(rowKeyOf(entityType, option.value))))
      );
    },
    defaultValue: [] as DropdownOption<string>[],
  });

  protected readonly entityOptions = computed(() => this.entityOptionsResource.value() ?? []);
  protected readonly entityOptionsLoading = computed(() => this.entityOptionsResource.isLoading());
  protected readonly entityOptionsFailed = computed(() => this.entityOptionsResource.error() != null);
  protected readonly canContinueAdd = computed(
    () => this.addEntityType() !== null && this.addEntityId() !== null && !this.entityOptionsLoading()
  );

  protected onRetry(): void {
    this.grantsResource.reload();
  }

  protected onDismissAlert(): void {
    this.alert.set(null);
  }

  protected onAdd(): void {
    this.alert.set(null);
    this.adding.set(true);
  }

  protected onAddTypeChange(entityType: EntityType | null): void {
    this.addEntityType.set(entityType);
    this.addEntityId.set(null);
  }

  protected onAddCancel(): void {
    this.adding.set(false);
    this.addEntityType.set(null);
    this.addEntityId.set(null);
  }

  protected onAddContinue(): void {
    const entityType = this.addEntityType();
    const entityId = this.addEntityId();
    if (!entityType || !entityId) return;
    const entityLabel =
      this.entityOptions().find(option => option.value === entityId)?.label ?? this.translate.instant('entityAccess.panel.restricted');
    this.openGrantDialog({ entityType, entityId, entityLabel, userId: this.userId(), userLabel: this.userLabel() }, true);
  }

  protected onEdit(row: PanelGrantRow): void {
    if (row.grant.viaGroup !== null) return;
    const { entityType, entityId } = row.grant;
    this.openGrantDialog(
      {
        entityType,
        entityId,
        entityLabel: row.label ?? this.translate.instant('entityAccess.panel.restricted'),
        userId: this.userId(),
        userLabel: this.userLabel(),
      },
      false
    );
  }

  protected onDelete(row: PanelGrantRow): void {
    if (row.grant.viaGroup !== null) return;
    const { entityType, entityId, etag } = row.grant;
    const label = row.label ?? this.translate.instant('entityAccess.panel.restricted');
    const dialogRef = this.dialog.open<boolean>(ConfirmDialogComponent, {
      width: 'min(96vw, 480px)',
      maxWidth: '96vw',
      panelClass: 'ai-chat-dialog-panel',
      backdropClass: 'ai-chat-dialog-backdrop',
      data: {
        title: this.translate.instant('entityAccess.panel.deleteTitle'),
        message: this.translate.instant('entityAccess.panel.deleteMessage', { label }),
        confirmText: this.translate.instant('entityAccess.panel.deleteConfirm'),
        destructive: true,
      },
    });
    dialogRef.closed.subscribe(confirmed => {
      if (!confirmed) return;
      this.deletingKey.set(this.rowKey(row));
      this.access.deleteGrants(entityType, entityId, { kind: 'user', id: this.userId() }, etag).subscribe({
        next: () => {
          this.deletingKey.set(null);
          this.toast.success(this.translate.instant('entityAccess.panel.deleteSuccess'));
          this.grantsResource.reload();
        },
        error: (error: HttpErrorResponse) => {
          this.deletingKey.set(null);
          if (isFullModeRejection(error)) this.showFullModeAlert();
          else this.toast.error(this.translate.instant('entityAccess.panel.deleteError'));
        },
      });
    });
  }

  protected rowKey(row: PanelGrantRow): string {
    return rowKeyOf(row.grant.entityType, row.grant.entityId, row.grant.viaGroup?.groupId);
  }

  protected isDeleting(row: PanelGrantRow): boolean {
    return this.deletingKey() === this.rowKey(row);
  }

  protected avatarVariant(row: PanelGrantRow): AvatarVariant {
    if (row.label === null) return 'error';
    if (row.grant.entityType === 'person') return 'brand';
    return 'info';
  }

  private openGrantDialog(data: EntityAccessDialogData, isAdd: boolean): void {
    const dialogRef = this.dialog.open<EntityAccessDialogCloseResult>(EntityAccessDialogComponent, {
      panelClass: 'ai-chat-dialog-panel',
      backdropClass: 'ai-chat-dialog-backdrop',
      data,
    });
    dialogRef.closed.subscribe(result => {
      const saved = result === true || (typeof result === 'object' && result !== null && result.saved);
      const fullMode = typeof result === 'object' && result !== null && result.fullMode === true;
      if (saved) {
        if (isAdd) {
          this.onAddCancel();
          this.toast.success(this.translate.instant('entityAccess.panel.addSuccess'));
        }
        this.grantsResource.reload();
        return;
      }
      if (fullMode) this.showFullModeAlert();
    });
  }

  private showFullModeAlert(): void {
    this.alert.set({
      badgeKey: 'entityAccess.panel.fullModeBadge',
      hintKey: 'entityAccess.panel.fullModeHint',
    });
  }

  private resolveLabel(entityType: EntityType, entityId: string): Observable<string> {
    return this.access.labelFor(entityType, entityId);
  }

  private loadEntityOptions(entityType: EntityType): Observable<DropdownOption<string>[]> {
    return this.access.searchEntities(entityType).pipe(map(options => options.map(option => ({ label: option.label, value: option.id }))));
  }
}
